import type { EarningsDay, EarningsLive } from "@/lib/api/earnings";

// Wording for the live "today so far" panel. The numbers all come from the
// server (the attendance engine's verdict for the punches so far); this only
// decides how they read.

/** 312 -> "5h 12m", 45 -> "45m", 120 -> "2h". */
export function minutesLabel(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** What today counts as if the employee checked out now. */
export function liveStatusLabel(status: string): string {
  switch (status) {
    case "present":
      return "Full day";
    case "half_day":
      return "Half day";
    case "absent":
      return "Less than a half day";
    default:
      return status.replace(/_/g, " ");
  }
}

/** The headline under the amount: what checking out now would pay. */
export function liveNowLine(live: EarningsLive, money: (n: number) => string): string {
  return live.amount > 0 ? `${money(live.amount)} if you check out now` : "Nothing for today yet if you check out now";
}

/** What reaching the next step would do, or that the full day is reached. */
export function liveNextLine(live: EarningsLive, money: (n: number) => string): string {
  if (!live.next_status || !live.minutes_to_next) return "Full day reached";
  const target = live.next_status === "half_day" ? "a half day" : "a full day";
  return `${minutesLabel(live.minutes_to_next)} more for ${target} (${money(live.next_amount ?? 0)} for today)`;
}

/** Share of a full day worked, for the progress bar. */
export function liveProgress(live: EarningsLive): number {
  if (!live.full_day_minutes) return 0;
  return Math.max(0, Math.min(100, (live.worked_minutes / live.full_day_minutes) * 100));
}

/** How often the card re-asks while the employee is checked in. */
export const LIVE_REFRESH_MS = 15_000;

/** How often the displayed figure advances between server answers. */
export const LIVE_TICK_MS = 5_000;

/** The month's earnings as of `nowMs`: the server's figure at as_of, advanced
 * by the accrual rate for the time since - but never past the next mark (the
 * server's minutes_to_next), where the rate changes and the server's next
 * answer takes over. Not a guess: it is the server's own rate. */
export function accruingEarned(live: EarningsLive, serverTotal: number, nowMs: number): number {
  const rate = live.accrued_per_minute ?? 0;
  if (rate <= 0) return serverTotal;
  const elapsedMin = Math.max(0, (nowMs - new Date(live.as_of).getTime()) / 60000);
  const room = live.minutes_to_next && live.minutes_to_next > 0 ? live.minutes_to_next : elapsedMin;
  return serverTotal + rate * Math.min(elapsedMin, room);
}

/** True when today is waiting only because the employee has not checked in
 * yet - so the card can say to check in, not that it awaits confirmation. */
export function notCheckedInToday(days: EarningsDay[], asOf: string): boolean {
  const today = days.find((d) => d.date === asOf);
  return !!today && today.state === "pending" && today.note === "Not checked in yet";
}

/** Today's accrued pay by time, as of `nowMs` (same advance as accruingEarned). */
export function accruedToday(live: EarningsLive, nowMs: number): number {
  return accruingEarned(live, live.accrued_amount, nowMs);
}
